// ─── Schedule Email Dialog ────────────────────────────────────────────────
// Dialog for scheduling email reminders and custom sends.
// Includes a list of currently scheduled emails with cancel buttons.

import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import ScheduleIcon from '@mui/icons-material/Schedule';
import {
  useScheduledEmails,
  useScheduleReminder,
  useScheduleSend,
  useCancelScheduledEmail,
} from '../../hooks/useEmailLifecycle.js';

// ─── Types ────────────────────────────────────────────────────────────────

interface ScheduleEmailDialogProps {
  open: boolean;
  onClose: () => void;
  eventId: string;
}

const STATUS_COLORS: Record<string, 'default' | 'success' | 'warning' | 'error'> = {
  PENDING: 'warning',
  SENT: 'success',
  CANCELLED: 'default',
  FAILED: 'error',
};

const EMAIL_TYPE_LABELS: Record<string, string> = {
  INVITE: 'Invite',
  REMINDER: 'Reminder',
  UPDATE: 'Update',
  RSVP_CONFIRMATION: 'RSVP Confirmation',
  HOST_NOTIFICATION: 'Host Notification',
  THANK_YOU: 'Thank You',
  CANCELLATION: 'Cancellation',
};

function formatScheduledDate(dateStr: string): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(dateStr));
}

// ─── Component ────────────────────────────────────────────────────────────

export default function ScheduleEmailDialog({
  open,
  onClose,
  eventId,
}: ScheduleEmailDialogProps) {
  const [tab, setTab] = useState(0);
  const [daysBefore, setDaysBefore] = useState(3);
  const [customDate, setCustomDate] = useState('');
  const [customEmailType, setCustomEmailType] = useState('REMINDER');
  const [successMessage, setSuccessMessage] = useState('');

  const { data: scheduledData, isLoading: loadingScheduled } = useScheduledEmails(eventId);
  const scheduleReminder = useScheduleReminder();
  const scheduleSend = useScheduleSend();
  const cancelScheduled = useCancelScheduledEmail();

  const scheduledEmails = scheduledData?.scheduledEmails ?? [];

  const handleScheduleReminder = async () => {
    setSuccessMessage('');
    try {
      await scheduleReminder.mutateAsync({ eventId, daysBefore });
      setSuccessMessage(`Reminder scheduled for ${daysBefore} day(s) before the event`);
    } catch {
      // Error shown via mutation state
    }
  };

  const handleScheduleCustom = async () => {
    if (!customDate) return;
    setSuccessMessage('');
    try {
      const sendAt = new Date(customDate).toISOString();
      await scheduleSend.mutateAsync({ eventId, sendAt, emailType: customEmailType });
      setSuccessMessage('Email scheduled successfully');
      setCustomDate('');
    } catch {
      // Error shown via mutation state
    }
  };

  const handleCancel = async (scheduledId: string) => {
    try {
      await cancelScheduled.mutateAsync({ scheduledId, eventId });
    } catch {
      // Error shown via mutation state
    }
  };

  const handleClose = () => {
    setSuccessMessage('');
    scheduleReminder.reset();
    scheduleSend.reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <ScheduleIcon />
          <span>Schedule Emails</span>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Tabs value={tab} onChange={(_e, v: number) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label="Reminder" />
          <Tab label="Custom" />
          <Tab label="Scheduled" />
        </Tabs>

        {/* Success / Error messages */}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}
        {scheduleReminder.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {(scheduleReminder.error as Error)?.message ?? 'Failed to schedule reminder'}
          </Alert>
        )}
        {scheduleSend.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {(scheduleSend.error as Error)?.message ?? 'Failed to schedule email'}
          </Alert>
        )}

        {/* Tab 0: Reminder */}
        {tab === 0 && (
          <Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Send a reminder email to all guests a set number of days before the event.
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                label="Days before event"
                type="number"
                value={daysBefore}
                onChange={(e) => setDaysBefore(Math.max(1, parseInt(e.target.value, 10) || 1))}
                slotProps={{ htmlInput: { min: 1, max: 90 } }}
                size="small"
                sx={{ width: 180 }}
              />
              <Button
                variant="contained"
                onClick={handleScheduleReminder}
                disabled={scheduleReminder.isPending}
                startIcon={
                  scheduleReminder.isPending ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <ScheduleIcon />
                  )
                }
              >
                Schedule
              </Button>
            </Stack>
          </Box>
        )}

        {/* Tab 1: Custom */}
        {tab === 1 && (
          <Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Schedule any email type to be sent at a specific date and time.
            </Typography>
            <Stack spacing={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Email Type</InputLabel>
                <Select
                  value={customEmailType}
                  onChange={(e) => setCustomEmailType(e.target.value)}
                  label="Email Type"
                >
                  <MenuItem value="REMINDER">Reminder</MenuItem>
                  <MenuItem value="UPDATE">Update</MenuItem>
                  <MenuItem value="INVITE">Invite</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Send At"
                type="datetime-local"
                value={customDate}
                onChange={(e) => setCustomDate(e.target.value)}
                size="small"
                fullWidth
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <Button
                variant="contained"
                onClick={handleScheduleCustom}
                disabled={scheduleSend.isPending || !customDate}
                startIcon={
                  scheduleSend.isPending ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <ScheduleIcon />
                  )
                }
              >
                Schedule
              </Button>
            </Stack>
          </Box>
        )}

        {/* Tab 2: Scheduled list */}
        {tab === 2 && (
          <Box>
            {loadingScheduled ? (
              <Stack alignItems="center" py={3}>
                <CircularProgress size={24} />
              </Stack>
            ) : scheduledEmails.length === 0 ? (
              <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
                No scheduled emails.
              </Typography>
            ) : (
              <List disablePadding>
                {scheduledEmails.map((se, idx) => (
                  <Box key={se.id}>
                    {idx > 0 && <Divider />}
                    <ListItem
                      secondaryAction={
                        se.status === 'PENDING' ? (
                          <Tooltip title="Cancel">
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => handleCancel(se.id)}
                              disabled={cancelScheduled.isPending}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : undefined
                      }
                    >
                      <ListItemText
                        primary={
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" fontWeight={600}>
                              {EMAIL_TYPE_LABELS[se.emailType] ?? se.emailType}
                            </Typography>
                            <Chip
                              label={se.status}
                              size="small"
                              color={STATUS_COLORS[se.status] ?? 'default'}
                              variant="outlined"
                            />
                          </Stack>
                        }
                        secondary={formatScheduledDate(se.scheduledFor)}
                      />
                    </ListItem>
                  </Box>
                ))}
              </List>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
